import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Calendar,
  Users,
  AlertTriangle,
  CheckCircle,
  Clock,
  HelpCircle,
  FileText,
  ChevronRight
} from 'lucide-react';
import { TeamMeetingService } from '@/services/team-meeting.service';
import { TeamDecisionService } from '@/services/team-decision.service';
import { TeamTaskService } from '@/services/team-task.service';
import { TeamRiskService } from '@/services/team-risk.service';
import { TeamQuestionService } from '@/services/team-question.service';
import { Meeting, MeetingStatus } from '@/models/team-meeting.model';
import { Decision } from '@/models/team-decision.model';
import { Task } from '@/models/team-task.model';
import { Risk } from '@/models/team-risk.model';
import { OpenQuestion } from '@/models/team-question.model';

type TabType = 'overview' | 'decisions' | 'tasks' | 'risks' | 'questions' | 'transcript';

export const MeetingDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [meeting, setMeeting] = useState<Meeting | null>(null);
  const [decisions, setDecisions] = useState<Decision[]>([]);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [risks, setRisks] = useState<Risk[]>([]);
  const [questions, setQuestions] = useState<OpenQuestion[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (id) {
      loadData();
    }
  }, [id]);

  const loadData = async () => {
    try {
      setLoading(true);

      // Load meeting
      const meetingData = await TeamMeetingService.getMeetingById(id!);
      setMeeting(meetingData);

      // Load extracted items
      const [decisionsData, tasksData, risksData, questionsData] = await Promise.all([
        TeamDecisionService.getDecisionsByMeeting(id!),
        TeamTaskService.getTasksByMeeting(id!),
        TeamRiskService.getRisksByMeeting(id!),
        TeamQuestionService.getQuestionsByMeeting(id!)
      ]);

      setDecisions(decisionsData);
      setTasks(tasksData);
      setRisks(risksData);
      setQuestions(questionsData);
    } catch (error) {
      console.error('Error loading meeting details:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date: Date | any) => {
    if (!date) return 'N/A';
    const d = date.toDate ? date.toDate() : new Date(date);
    return d.toLocaleDateString('en-US', {
      month: 'long',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const getStatusBadge = (status: MeetingStatus) => {
    const badges: Record<MeetingStatus, { color: string; label: string }> = {
      [MeetingStatus.scheduled]: { color: 'bg-gray-100 text-gray-800', label: 'Scheduled' },
      [MeetingStatus.processing]: { color: 'bg-blue-100 text-blue-800', label: 'Processing' },
      [MeetingStatus.processed]: { color: 'bg-green-100 text-green-800', label: 'Processed' },
      [MeetingStatus.archived]: { color: 'bg-gray-100 text-gray-600', label: 'Archived' },
      [MeetingStatus.error]: { color: 'bg-red-100 text-red-800', label: 'Error' },
    };
    const badge = badges[status];
    return (
      <span className={`px-3 py-1 rounded-full text-sm font-medium ${badge.color}`}>
        {badge.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading meeting details...</p>
        </div>
      </div>
    );
  }

  if (!meeting) {
    return (
      <div className="p-6">
        <div className="text-center py-12">
          <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-medium text-gray-900 mb-2">Meeting not found</h3>
          <button
            onClick={() => navigate('/team-workspace')}
            className="text-blue-600 hover:text-blue-700"
          >
            Return to workspace
          </button>
        </div>
      </div>
    );
  }

  const tabs = [
    { id: 'overview' as TabType, label: 'Overview', icon: FileText },
    { id: 'decisions' as TabType, label: `Decisions (${decisions.length})`, icon: CheckCircle },
    { id: 'tasks' as TabType, label: `Tasks (${tasks.length})`, icon: Clock },
    { id: 'risks' as TabType, label: `Risks (${risks.length})`, icon: AlertTriangle },
    { id: 'questions' as TabType, label: `Questions (${questions.length})`, icon: HelpCircle },
    { id: 'transcript' as TabType, label: 'Transcript', icon: FileText },
  ];

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="mb-6">
        <button
          onClick={() => navigate('/team-workspace')}
          className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Workspace
        </button>

        <div className="bg-white rounded-lg shadow p-6 border border-gray-200">
          <div className="flex items-start justify-between mb-4">
            <div className="flex-1">
              <h1 className="text-2xl font-bold text-gray-900 mb-2">{meeting.title}</h1>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <Calendar className="w-4 h-4" />
                  {formatDate(meeting.meetingDate)}
                </span>
                {meeting.attendees && meeting.attendees.length > 0 && (
                  <span className="flex items-center gap-1">
                    <Users className="w-4 h-4" />
                    {meeting.attendees.length} attendees
                  </span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {getStatusBadge(meeting.status)}
              {meeting.containsPHI && (
                <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-red-100 text-red-800">
                  <AlertTriangle className="w-4 h-4 mr-1" />
                  PHI Detected
                </span>
              )}
            </div>
          </div>

          {meeting.notes && (
            <div className="mt-4 p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-700">{meeting.notes}</p>
            </div>
          )}
        </div>
      </div>

      {/* PHI Warning */}
      {meeting.containsPHI && meeting.phiFlags && meeting.phiFlags.length > 0 && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <AlertTriangle className="w-5 h-5 text-red-600 mt-0.5" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-red-900 mb-1">
                Protected Health Information Detected
              </h3>
              <p className="text-sm text-red-700 mb-2">
                This meeting transcript contains {meeting.phiFlags.length} PHI flag(s).
                Please review and handle according to HIPAA guidelines.
              </p>
              <div className="space-y-1">
                {meeting.phiFlags.slice(0, 3).map((flag, idx) => (
                  <div key={idx} className="text-xs text-red-600">
                    • {flag.type} - {flag.location} (Confidence: {flag.confidence})
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="bg-white rounded-lg shadow border border-gray-200">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveTab(tab.id)}
                  className={`flex items-center gap-2 px-6 py-4 border-b-2 font-medium text-sm ${
                    activeTab === tab.id
                      ? 'border-blue-600 text-blue-600'
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                  }`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>

        <div className="p-6">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="bg-blue-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <CheckCircle className="w-6 h-6 text-blue-600" />
                    <span className="text-2xl font-bold text-blue-900">{decisions.length}</span>
                  </div>
                  <p className="text-sm text-blue-700 mt-2">Decisions</p>
                </div>

                <div className="bg-green-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <Clock className="w-6 h-6 text-green-600" />
                    <span className="text-2xl font-bold text-green-900">{tasks.length}</span>
                  </div>
                  <p className="text-sm text-green-700 mt-2">Tasks</p>
                </div>

                <div className="bg-red-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <AlertTriangle className="w-6 h-6 text-red-600" />
                    <span className="text-2xl font-bold text-red-900">{risks.length}</span>
                  </div>
                  <p className="text-sm text-red-700 mt-2">Risks</p>
                </div>

                <div className="bg-purple-50 rounded-lg p-4">
                  <div className="flex items-center justify-between">
                    <HelpCircle className="w-6 h-6 text-purple-600" />
                    <span className="text-2xl font-bold text-purple-900">{questions.length}</span>
                  </div>
                  <p className="text-sm text-purple-700 mt-2">Questions</p>
                </div>
              </div>

              {meeting.attendees && meeting.attendees.length > 0 && (
                <div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-3">Attendees</h3>
                  <div className="flex flex-wrap gap-2">
                    {meeting.attendees.map((attendee, idx) => (
                      <span
                        key={idx}
                        className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                      >
                        {attendee}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Decisions Tab */}
          {activeTab === 'decisions' && (
            <div className="space-y-4">
              {decisions.length === 0 ? (
                <div className="text-center py-12">
                  <CheckCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No decisions extracted from this meeting</p>
                </div>
              ) : (
                decisions.map((decision) => (
                  <div key={decision.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{decision.title}</h3>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        decision.impact === 'critical' ? 'bg-red-100 text-red-800' :
                        decision.impact === 'high' ? 'bg-orange-100 text-orange-800' :
                        decision.impact === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {decision.impact} impact
                      </span>
                    </div>
                    <p className="text-gray-700 mb-2">{decision.description}</p>
                    {decision.rationale && (
                      <p className="text-sm text-gray-600 italic">
                        Rationale: {decision.rationale}
                      </p>
                    )}
                    {decision.sourceTextSnippet && (
                      <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
                        Source: "{decision.sourceTextSnippet}"
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Tasks Tab */}
          {activeTab === 'tasks' && (
            <div className="space-y-4">
              {tasks.length === 0 ? (
                <div className="text-center py-12">
                  <Clock className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No tasks extracted from this meeting</p>
                </div>
              ) : (
                tasks.map((task) => (
                  <div key={task.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{task.title}</h3>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        task.priority === 'urgent' ? 'bg-red-100 text-red-800' :
                        task.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                        task.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {task.priority} priority
                      </span>
                    </div>
                    <p className="text-gray-700 mb-2">{task.description}</p>
                    {task.assigneeName && (
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Assigned to:</span> {task.assigneeName}
                      </p>
                    )}
                    {task.sourceTextSnippet && (
                      <div className="mt-2 p-2 bg-gray-50 rounded text-xs text-gray-600">
                        Source: "{task.sourceTextSnippet}"
                      </div>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Risks Tab */}
          {activeTab === 'risks' && (
            <div className="space-y-4">
              {risks.length === 0 ? (
                <div className="text-center py-12">
                  <AlertTriangle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No risks identified in this meeting</p>
                </div>
              ) : (
                risks.map((risk) => (
                  <div key={risk.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{risk.title}</h3>
                      <div className="flex gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          risk.severity === 'critical' ? 'bg-red-100 text-red-800' :
                          risk.severity === 'high' ? 'bg-orange-100 text-orange-800' :
                          risk.severity === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                          'bg-gray-100 text-gray-800'
                        }`}>
                          {risk.severity} severity
                        </span>
                        <span className="px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          {risk.likelihood} likelihood
                        </span>
                      </div>
                    </div>
                    <p className="text-gray-700 mb-2">{risk.description}</p>
                    {risk.potentialImpact && (
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Impact:</span> {risk.potentialImpact}
                      </p>
                    )}
                    {risk.mitigationStrategy && (
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Mitigation:</span> {risk.mitigationStrategy}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Questions Tab */}
          {activeTab === 'questions' && (
            <div className="space-y-4">
              {questions.length === 0 ? (
                <div className="text-center py-12">
                  <HelpCircle className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No open questions from this meeting</p>
                </div>
              ) : (
                questions.map((question) => (
                  <div key={question.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <h3 className="text-lg font-semibold text-gray-900">{question.question}</h3>
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        question.priority === 'high' ? 'bg-orange-100 text-orange-800' :
                        question.priority === 'medium' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {question.priority} priority
                      </span>
                    </div>
                    {question.context && (
                      <p className="text-gray-700 mb-2">{question.context}</p>
                    )}
                    {question.assignedTo && (
                      <p className="text-sm text-gray-600">
                        <span className="font-medium">Assigned to:</span> {question.assignedTo}
                      </p>
                    )}
                  </div>
                ))
              )}
            </div>
          )}

          {/* Transcript Tab */}
          {activeTab === 'transcript' && (
            <div className="bg-gray-50 rounded-lg p-6">
              {meeting.transcriptText ? (
                <pre className="whitespace-pre-wrap text-sm text-gray-700 font-mono">
                  {meeting.transcriptText}
                </pre>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                  <p className="text-gray-600">No transcript available</p>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MeetingDetail;
